// SPDX-License-Identifier: MIT
import { supabase } from '../auth/supabase';

export interface Entitlement {
  freeQuota: number;
  purchasedCredits: number;
  ideasConsumed: number;
  remaining: number;
  desktopLicense: boolean;
  cloudStatus: 'none' | 'active';
  cloudUntil: string | null;
}

/** Safe default returned when the user is not authenticated. */
const ANON_DEFAULT: Entitlement = {
  freeQuota: 1,
  purchasedCredits: 0,
  ideasConsumed: 0,
  remaining: 1,
  desktopLicense: false,
  cloudStatus: 'none',
  cloudUntil: null,
};

/**
 * Read the current user's entitlement row from `public.user_entitlements`.
 * Returns a safe default when the user is not authenticated or when no row
 * exists yet (new user — free tier defaults apply).
 */
export async function getEntitlement(): Promise<Entitlement> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return ANON_DEFAULT;

  const { data, error } = await supabase
    .from('user_entitlements')
    .select('free_quota,purchased_credits,ideas_consumed,desktop_license,cloud_status,cloud_until')
    .eq('user_id', user.id)
    .maybeSingle();
  if (error) {
    console.warn('[entitlements] getEntitlement failed', error);
  }

  const fq = data?.free_quota ?? 1;
  const pc = data?.purchased_credits ?? 0;
  const ic = data?.ideas_consumed ?? 0;

  return {
    freeQuota: fq,
    purchasedCredits: pc,
    ideasConsumed: ic,
    remaining: Math.max(0, fq + pc - ic),
    desktopLicense: data?.desktop_license ?? false,
    cloudStatus: (data?.cloud_status ?? 'none') as 'none' | 'active',
    cloudUntil: data?.cloud_until ?? null,
  };
}
